/**
 * alyx — PWA: service worker + notificaciones push
 * =================================================
 * Vive en: assets/alyx-pwa.js
 * - Registra el service worker servido por el app proxy (mismo origen).
 * - Miembros logueados: pide el permiso de notificaciones automaticamente
 *   (en el primer toque de cualquier pagina; si ya esta concedido, suscribe
 *   directo al cargar).
 * - Usa el bearer de alyx-order-tests.js (mismas claves de sessionStorage).
 */
(function () {
  'use strict';

  var API_BASE = 'https://alyx.gedl.cl';
  var SW_URL = '/apps/alyx-pwa/tools/sw.js';
  var STORAGE_KEY = 'alyx_pwa_prompted';

  function getBearer() {
    var own = sessionStorage.getItem('alyx_bearer_token');

    if (own) {
      return own;
    }

    var shared = sessionStorage.getItem('alyx_api_bearer');

    if (shared) {
      try {
        var parsed = JSON.parse(shared);

        if (parsed && typeof parsed.token === 'string') {
          return parsed.token;
        }
      } catch (e) {
        /* no es JSON */
      }
    }

    return null;
  }

  function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.register(SW_URL).catch(function () {
      /* fallo silencioso (proxy no configurado, etc.) */
    });
  }

  function urlBase64ToUint8Array(base64) {
    var padding = '='.repeat((4 - (base64.length % 4)) % 4);
    var base64Url = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    var raw = atob(base64Url);
    var array = new Uint8Array(raw.length);

    for (var i = 0; i < raw.length; i++) {
      array[i] = raw.charCodeAt(i);
    }

    return array;
  }

  function withTimeout(promise, ms, label) {
    return Promise.race([
      promise,
      new Promise(function (resolve, reject) {
        setTimeout(function () {
          reject(new Error(label + ' (timeout after ' + ms + 'ms)'));
        }, ms);
      })
    ]);
  }

  function subscribe() {
    var bearer = getBearer();
    var vapid = window.ALYX_VAPID_PUBLIC || '';

    if (!('serviceWorker' in navigator)) {
      throw new Error('this browser has no Service Worker support.');
    }

    if (!('PushManager' in window)) {
      throw new Error('Push API not supported in this browser.');
    }

    if (!vapid) {
      throw new Error('VAPID public key is missing in the theme.');
    }

    if (!bearer) {
      throw new Error('no session token. Please log in again on the tests page.');
    }

    return navigator.serviceWorker.register(SW_URL, { updateViaCache: 'none' }).then(function (registration) {
      return withTimeout(registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid)
      }), 20000, 'push subscribe timed out');
    }).then(function (subscription) {
      return fetch(API_BASE + '/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + bearer
        },
        body: JSON.stringify(subscription.toJSON())
      });
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('server responded ' + response.status + '. Please try again.');
      }

      return true;
    });
  }

  function requestPermission() {
    if (!('Notification' in window)) {
      return Promise.resolve(false);
    }

    if (Notification.permission === 'granted') {
      return subscribe();
    }

    if (Notification.permission === 'denied') {
      return Promise.resolve(false);
    }

    return Notification.requestPermission().then(function (permission) {
      if (permission !== 'granted') {
        return false;
      }

      return subscribe();
    });
  }

  function showToast(msg) {
    var el = document.getElementById('alyx-pwa-toast');

    if (!el) {
      el = document.createElement('div');
      el.id = 'alyx-pwa-toast';
      el.style.cssText = 'position:fixed;left:50%;bottom:1rem;transform:translateX(-50%);padding:0.6rem 1rem;border-radius:8px;background:#000;color:#fff;font-size:0.85rem;z-index:10000;max-width:90%;text-align:center;';
      document.body.appendChild(el);
    }

    el.textContent = msg;

    clearTimeout(el._t);
    el._t = setTimeout(function () {
      el.remove();
    }, 4000);
  }

  function enableNotifications() {
    localStorage.setItem(STORAGE_KEY, '1');

    requestPermission().then(function (ok) {
      if (ok) {
        showToast('Notifications enabled.');
      } else {
        showToast('Notifications are off. You can enable them in your browser settings.');
      }
    }).catch(function (err) {
      if (err && err.message && err.message.indexOf('no session token') !== -1) {
        showToast('Notifications enabled.');
        return;
      }

      showToast('Error: ' + (err && err.message ? err.message : String(err)));
    });
  }

  function maybeEnableNotifications() {
    if (localStorage.getItem(STORAGE_KEY) || !('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      localStorage.setItem(STORAGE_KEY, '1');

      if (!getBearer()) {
        return;
      }

      subscribe().then(function (ok) {
        if (!ok) {
          showToast('Notifications are off. You can enable them in your browser settings.');
        }
      }).catch(function (err) {
        showToast('Error: ' + (err && err.message ? err.message : String(err)));
      });

      return;
    }

    if (Notification.permission === 'denied') {
      localStorage.setItem(STORAGE_KEY, '1');
      return;
    }

    var fired = false;

    var attempt = function () {
      if (fired) {
        return;
      }

      fired = true;
      document.removeEventListener('click', attempt, true);
      document.removeEventListener('touchstart', attempt, true);

      enableNotifications();
    };

    document.addEventListener('click', attempt, true);
    document.addEventListener('touchstart', attempt, true);
  }

  function setupInstallButton() {
    var deferredPrompt = null;

    window.addEventListener('beforeinstallprompt', function (event) {
      event.preventDefault();
      deferredPrompt = event;

      var btn = document.getElementById('alyx-install-btn');

      if (btn) {
        return;
      }

      btn = document.createElement('button');
      btn.id = 'alyx-install-btn';
      btn.textContent = 'Install app';
      btn.setAttribute('aria-label', 'Install the Alyx app');
      btn.style.cssText = 'position:fixed;right:1rem;bottom:1rem;padding:0.6rem 1.2rem;border-radius:8px;background:#000;color:#fff;border:none;cursor:pointer;font-weight:600;z-index:9999;box-shadow:0 2px 8px rgba(0,0,0,0.25);';

      btn.addEventListener('click', function () {
        if (!deferredPrompt) {
          return;
        }

        deferredPrompt.prompt();
        deferredPrompt.userChoice.then(function () {
          deferredPrompt = null;
          btn.remove();
        });
      });

      document.body.appendChild(btn);
    });

    window.addEventListener('appinstalled', function () {
      var btn = document.getElementById('alyx-install-btn');

      if (btn) {
        btn.remove();
      }
    });
  }

  function init() {
    registerServiceWorker();
    setupInstallButton();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', maybeEnableNotifications);
    } else {
      maybeEnableNotifications();
    }
  }

  init();
})();